// ============================================================
// DM-LMS — Shared Validation Schemas (Zod)
// DRY: one schema used by both API (Hono) and Web (React Hook Form).
// ============================================================

import { z } from "zod";
import { ROLES, DELIVERY_MODES } from "../types/index.js";

// ---- Primitives ----
export const phoneSchema = z
  .string()
  .regex(/^\+254\d{9}$/, "Phone must be in format +254XXXXXXXXX");

export const codeSchema = z
  .string()
  .regex(/^[A-Z]+-\d{4}$/, "Invalid entity code format");

// ---- Auth ----
export const completeProfileSchema = z.object({
  phone: phoneSchema,
  name:  z.string().min(2).max(100),
});

// ---- Course ----
export const createCourseSchema = z.object({
  title:          z.string().min(3).max(200),
  description:    z.string().min(10).max(2000),
  mode:           z.enum(DELIVERY_MODES),
  capacity:       z.number().int().min(1).max(200),
  prerequisiteId: z.string().uuid().nullable(),
  durationHours:  z.number().int().min(1).max(500),
  price:          z.number().int().min(0),             // KES, no decimals
});

export const updateCourseSchema = createCourseSchema.partial();

// ---- Session ----
export const createSessionSchema = z.object({
  courseId:        z.string().uuid(),
  title:           z.string().min(3).max(200),
  scheduledAt:     z.string().datetime(),
  durationMinutes: z.number().int().min(15).max(480),
  mode:            z.enum(DELIVERY_MODES),
  meetingUrl:      z.string().url().nullable(),
  venue:           z.string().max(500).nullable(),
});

// ---- Enrollment ----
export const enrollSchema = z.object({
  courseId: z.string().uuid(),
});

// ---- Attendance ----
export const checkInSchema = z.object({
  sessionId: z.string().uuid(),
  studentId: z.string().uuid(),
});

// ---- Payment ----
export const initiateMpesaSchema = z.object({
  enrollmentId: z.string().uuid(),
  phone:        phoneSchema,
  amountKes:    z.number().int().min(1),
});

export const initiateStripeSchema = z.object({
  enrollmentId: z.string().uuid(),
  amountKes:    z.number().int().min(1),
});

// ---- Pagination ----
export const paginationSchema = z.object({
  page:     z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

// ---- Role ----
export const roleSchema = z.enum(ROLES);

// ---- Inferred types (DRY — derive types from schemas) ----
export type CompleteProfileInput  = z.infer<typeof completeProfileSchema>;
export type CreateCourseInput     = z.infer<typeof createCourseSchema>;
export type UpdateCourseInput     = z.infer<typeof updateCourseSchema>;
export type CreateSessionInput    = z.infer<typeof createSessionSchema>;
export type EnrollInput           = z.infer<typeof enrollSchema>;
export type CheckInInput          = z.infer<typeof checkInSchema>;
export type InitiateMpesaInput    = z.infer<typeof initiateMpesaSchema>;
export type InitiateStripeInput   = z.infer<typeof initiateStripeSchema>;
export type PaginationInput       = z.infer<typeof paginationSchema>;
